import { AxiosRequestConfig } from 'axios';
import { apiService } from './api.service';
import { PaginatedResponse } from 'types/index';
import { Employee } from 'types/employee'; 
import { collectDeviceMetadata, DeviceMetadata } from 'utils/deviceMetadata';

class EmployeesService {
  async list(page: number = 1, limit: number = 100): Promise<PaginatedResponse<Employee>> {
    const response = await apiService.get<PaginatedResponse<Employee>>(`/employee/GetPageList?page=${page}&pageSize=${limit}`);
 
    if ((response as any)?.data && (response as any)?.success !== false && (response as any).data?.data && (response as any).data?.pagination) {
      return (response as any).data as PaginatedResponse<Employee>;
    } 
    const maybeItems = (response as any)?.items;
    const maybeTotal = (response as any)?.totalCount;
    if (Array.isArray(maybeItems) && typeof maybeTotal === 'number') {
      const totalPages = Math.max(1, Math.ceil(maybeTotal / limit));
      return {
        data: maybeItems as Employee[],
        pagination: { page, limit, total: maybeTotal, totalPages },
      };
    }
 
    const data = (response as any)?.data || response;
    if (Array.isArray(data)) {
      return {
        data: data as Employee[],
        pagination: { page, limit, total: data.length, totalPages: 1 },
      };
    }

    throw new Error((response as any)?.message || 'Failed to load employees');
  }

  async getById(id: string): Promise<Employee> {
    const response = await apiService.get<any>(`/employee/${id}`);
    const payload: any = (response as any)?.data ?? response;
 
    const employeeData =
      payload?.data && typeof payload.data === 'object'
        ? payload.data
        : typeof payload === 'object'
        ? payload
        : null;

    const encryptedId =
      (payload as any)?.encryptedId ??
      (response as any)?.encryptedId ??
      (employeeData as any)?.encryptedId ??
      null;

    if (employeeData) {
      return { ...employeeData, encryptedId } as Employee;
    }
 
    return response as unknown as Employee;
  }


  async getByPublicKey(key: string): Promise<Employee> { 
    const tryParse = (response: any): Employee => {
      const payload = (response as any)?.data ?? response;
      if (payload && typeof payload === 'object') {
        const licenseExpiredNode =
          (response as any).licenseExpired ??
          (response as any).LicenseExpired ??
          (payload as any).licenseExpired ??
          (payload as any).LicenseExpired ??
          false;
        const isDemoNode =
          (response as any).isDemo ??
          (response as any).IsDemo ??
          (payload as any).isDemo ??
          (payload as any).IsDemo ??
          false;
        const encryptedIdNode =
          (response as any).encryptedId ??
          (response as any).EncryptedId ??
          (payload as any).encryptedId ??
          (payload as any).EncryptedId ??
          null;
        return {
          ...(payload as Employee),
          encryptedId: encryptedIdNode,
          licenseExpired: Boolean(licenseExpiredNode),
          isDemo: Boolean(isDemoNode),
        } as Employee;
      }
      return response as unknown as Employee;
    };

    const metadata = await collectDeviceMetadata();
    const requestConfig = this.buildMetadataConfig(metadata);

    try {
      const response = await apiService.get<any>(`/employee/Encrypted/${encodeURIComponent(key)}`, requestConfig);
      return tryParse(response);
    } catch (e: any) {
      // Retry without explicit encoding (in case upstream already encoded/handles raw path)
      try {
        const response2 = await apiService.get<any>(`/employee/Encrypted/${key}`, requestConfig);
        return tryParse(response2);
      } catch {
        // Final retry with lowercase path segment for case-insensitive/alternate routing setups
        const response3 = await apiService.get<any>(`/employee/encrypted/${encodeURIComponent(key)}`, requestConfig);
        return tryParse(response3);
      }
    }
  }

  async create(payload: Partial<Employee>): Promise<Employee> {
    const response = await apiService.post<Employee>(`/employee`, payload);
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as Employee;
    }
    return (response as any) as Employee;
  }

  async uploadImage(employeeId: string, imageData: string): Promise<Employee> {
    const response = await apiService.post<Employee>(`/employee/${employeeId}/image`, {
      imageData
    });
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as Employee;
    }
    return (response as any) as Employee;
  }

  async update(id: string, payload: Partial<Employee>): Promise<Employee> {
    const response = await apiService.put<Employee>(`/employee/${id}`, payload);
    if ((response as any)?.data && (response as any)?.success !== false) {
      return (response as any).data as Employee;
    }
    return (response as any) as Employee;
  }

  async remove(id: string): Promise<void> {
    const response = await apiService.delete(`/employee/${id}`);
    if ((response as any)?.success === false) {
      throw new Error((response as any)?.message || 'Delete failed');
    }
  }

  private buildMetadataConfig(metadata: DeviceMetadata): AxiosRequestConfig {
    const params: Record<string, string> = {};
    const headers: Record<string, string> = {};

    const addParam = (key: string, value?: string) => {
      if (value && value.trim().length > 0) {
        params[key] = value;
      }
    };

    addParam('macAddress', metadata.macAddress);
    addParam('deviceId', metadata.deviceId);
    addParam('deviceName', metadata.deviceName);
    addParam('platform', metadata.platform);
    addParam('language', metadata.language);
    addParam('screenResolution', metadata.screenResolution);
    addParam('timezone', metadata.timezone);
    if (typeof metadata.timezoneOffsetMinutes === 'number') {
      addParam('timezoneOffset', metadata.timezoneOffsetMinutes.toString());
    }
    addParam('appVersion', metadata.appVersion);
    addParam('osVersion', metadata.osVersion);
    addParam('browserVersion', metadata.browserVersion);
    addParam('extra', metadata.extra);
    addParam('continent', metadata.continent);
    addParam('country', metadata.country);
    addParam('city', metadata.city);
    addParam('scannedAt', metadata.scannedAtLocal);
    addParam('clientIp', metadata.clientIp);

    if (metadata.fingerprint) {
      headers['X-Device-Fingerprint'] = metadata.fingerprint;
      addParam('fingerprint', metadata.fingerprint);
    }

    const config: AxiosRequestConfig = {};
    if (Object.keys(params).length) {
      config.params = params;
    }
    if (Object.keys(headers).length) {
      config.headers = headers;
    }

    return config;
  }
}

export const employeesService = new EmployeesService();
export default employeesService;


